/**
 * Реализация IPlatformEnvironment для браузера.
 * Использует стандартные браузерные API.
 */
import log from "@fxid/shared";
export class BrowserEnvironment {
    constructor() {
        this.consentChangeCallbacks = [];
    }
    // ========== Навигация и URL ==========
    getLocationHash() {
        return window.location.hash;
    }
    setLocationHash(hash) {
        window.location.hash = hash;
    }
    getLocationSearch() {
        return window.location.search;
    }
    getLocationHref() {
        return window.location.href;
    }
    reload() {
        window.location.reload();
    }
    // ========== История навигации ==========
    replaceHistoryState(state, title, url) {
        window.history.replaceState(state, title, url);
    }
    getHistoryState() {
        return window.history.state;
    }
    // ========== События ==========
    addEventListener(event, handler) {
        window.addEventListener(event, handler);
    }
    removeEventListener(event, handler) {
        window.removeEventListener(event, handler);
    }
    postMessage(data, targetOrigin) {
        window.postMessage(data, targetOrigin);
    }
    // ========== Хранилище ==========
    getStorageItem(key) {
        try {
            return localStorage.getItem(key);
        }
        catch (e) {
            log.warn("localStorage not available:", e);
            return null;
        }
    }
    setStorageItem(key, value) {
        try {
            localStorage.setItem(key, value);
        }
        catch (e) {
            log.warn("localStorage not available:", e);
        }
    }
    removeStorageItem(key) {
        try {
            localStorage.removeItem(key);
        }
        catch (e) {
            log.warn("localStorage not available:", e);
        }
    }
    // ========== Загрузка скриптов ==========
    async loadScript(url) {
        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.src = url;
            script.async = true;
            script.addEventListener("load", () => resolve(), false);
            script.addEventListener("error", () => reject(new Error(`Failed to load script: ${url}`)), false);
            const head = document.getElementsByTagName("head")[0];
            head.appendChild(script);
        });
    }
    canLoadExternalScripts() {
        return true;
    }
    // ========== Аналитика ==========
    sendAnalyticsEvent(eventName, eventData) {
        // Google Analytics через dataLayer
        if (window.dataLayer) {
            window.dataLayer.push({
                event: eventName,
                ...eventData
            });
        }
        // Можно добавить другие системы аналитики
        if (window.gtag) {
            window.gtag('event', eventName, eventData);
        }
    }
    isAnalyticsAvailable() {
        return !!window.dataLayer || !!window.gtag;
    }
    // ========== UI и стили ==========
    getComputedStyleProperty(element, property) {
        if (!element) {
            element = document.documentElement;
        }
        return getComputedStyle(element).getPropertyValue(property);
    }
    getCSSVariable(variableName) {
        return getComputedStyle(document.documentElement).getPropertyValue(variableName);
    }
    async exitFullscreen() {
        const cancellFullScreen = document.exitFullscreen ||
            document.mozCancelFullScreen ||
            document.webkitExitFullscreen ||
            document.msExitFullscreen;
        if (cancellFullScreen) {
            await cancellFullScreen.call(document);
        }
    }
    isFullscreenSupported() {
        return !!(document.exitFullscreen ||
            document.mozCancelFullScreen ||
            document.webkitExitFullscreen ||
            document.msExitFullscreen);
    }
    // ========== Consent Management ==========
    getConsentStatus() {
        // Проверяем наличие Consent Management Platform
        if (window.__cmp) {
            const status = window.__cmp("consentStatus", null, null, false);
            if (typeof status === "object" && "consentExists" in status) {
                return {
                    consentExists: status.consentExists,
                    consentRequired: status.consentRequired || false,
                    googlePersonalization: status.googlePersonalization,
                    googleAds: status.googleAds,
                    analytics: status.analytics
                };
            }
        }
        // Если CMP нет, считаем что согласие не требуется
        return {
            consentExists: false,
            consentRequired: false,
            googlePersonalization: true,
            googleAds: true,
            analytics: true
        };
    }
    onConsentChange(callback) {
        this.consentChangeCallbacks.push(callback);
        // Подписываемся на события CMP
        if (window.__cmp) {
            window.__cmp("addEventListener", ["consent"], (e, success) => {
                if (success) {
                    const status = this.getConsentStatus();
                    if (status) {
                        this.consentChangeCallbacks.forEach(cb => cb(status));
                    }
                }
            }, false);
        }
    }
    // ========== Информация о платформе ==========
    getPlatformInfo() {
        const userAgent = navigator.userAgent;
        const platform = navigator.platform;
        const language = navigator.language;
        // Определяем тип браузера
        let browserVersion = "unknown";
        if (userAgent.indexOf("Chrome") > -1) {
            browserVersion = "Chrome";
        }
        else if (userAgent.indexOf("Safari") > -1) {
            browserVersion = "Safari";
        }
        else if (userAgent.indexOf("Firefox") > -1) {
            browserVersion = "Firefox";
        }
        else if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident/") > -1) {
            browserVersion = "IE";
        }
        return {
            type: "browser",
            version: browserVersion,
            userAgent,
            platform,
            language
        };
    }
    isMobile() {
        // Простая проверка на мобильное устройство
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
    getLanguage() {
        return navigator.language || "en-US";
    }
}
